import {
    createTitleAmountViewModel,
    createTagsViewModel,
    createTitlesViewModel,
} from 'factories';

import {
    pipe,
    pickBy,
    uniq,
    props,
    ascend,
    slice,
    descend,
    filter,
    sort,
    find,
    findIndex,
    propEq,
    isNil,
    prop,
    toPairs,
    map
} from 'core/utils';

export const mediateEntriesInfoDependent = pipe(
    prop('entries')
);

export const mediateEntriesAdInfoDependent = (id) => pipe(
    prop('entriesAds'),
    find(propEq('id', id))
);

export const mediateEntriesTitleAmountInfoDependent = pipe(
    prop('entries'),
    createTitleAmountViewModel
);

export const mediateTagsInfoDependent = pipe(
    prop('entries'),
    createTagsViewModel
);

export const mediateEditEntryInfoDependent = prop('editTask');

export const mediateEditEntryPaymentsInfoDependent = prop('editPaymentsTask');

export const mediateParentEntryInfoDependent = (parentID) => pipe(
    prop('entries'),
    find(propEq('id', parentID))
);

export const mediatePaymentsAllFieldsForEntryInfoDependent = (id) => pipe(
    prop('entries'),
    (entries) => {
        let entry = find(propEq('id', id), entries);
        if(!entry) {
            entry = {
                payments : [],
                id
            } 
        }
        return entry.payments.map((payment) => {
            let entryPayment = payment;

            if(payment.id) {
                entryPayment = find(propEq('id', payment.id), entries);
            }

            return entryPayment;
        });
    },
    sort(ascend(prop('order'))),
);

export const mediateParentEntriesInfoDependent = pipe(
    prop('entries'),
    filter(
        pipe(
            prop('parent'), 
            isNil
        )
    ),
    sort(ascend(prop('order'))),
);

export const mediateNotCompleteParentEntriesInfoDependent = pipe(
    mediateParentEntriesInfoDependent,
    filter(propEq('complete', false))
);

export const mediateByIDEntryInfoDependent = mediateParentEntryInfoDependent;
export const mediateLastCompletedEntryInfoDependent = pipe(
    prop('entries'),
    sort(descend(prop('order'))),
    find(propEq('complete', true))
);
export const mediateBalancesInfoDependent = prop('balances');
export const mediateBalanceForEntryInfoDependent = (id) => pipe(
    prop('balances'),
    (balances) => {
        let amount = Number(balances[id]);
        return isNaN(amount) ? NaN : amount;
    }
)
export const mediateStatsInfoDependent = prop('entriesStats');
export const mediateEntriesWithBalanceUntilLastCompletedInfoDependent = pipe(
    props(['entries', 'balances']),
    ([entries, balances]) => {
        let entriesForCharting = [];
        for(let [key, val] of Object.entries(balances)) {
            let entry = entries.find(propEq('id', key));
            entriesForCharting.push({
                ...entry,
                balance : val
            })
        }

        return pipe(
            sort(descend(prop('order'))),
            findIndex(propEq('complete', true)),
            (sliceIndex) => {
                return slice(0, entriesForCharting.length - sliceIndex, entriesForCharting);
            }
        )(entriesForCharting);
    }
);

export const mediateEntriesTitlesAndTagsInfoDependent = pipe(
    prop('entries'),
    (entries) => {
        return [
            ...createTagsViewModel(entries),
            ...createTitlesViewModel(entries)
        ]
    },
    uniq
);

export const mediateNegBalancesHistoryInfoDependent = pipe(
    prop('balanceHistory'),
    ([last, current]) => {
        return current - last;
    }
);

export const mediateReminderActiveInfoDependent = pipe(
    prop('entries'),
    filter((entry) => {
        return entry.isReminderOn && !entry.complete && new Date(entry.reminderDate).getTime() <= new Date().getTime();
    })
);

export const mediateDueTodayInfoDependent = pipe(
    prop('entries'),
    filter((entry) => {
        let dueDate = new Date(entry.dueDate);
        let now = new Date();
        return entry.dueDate && 
            dueDate.getFullYear() === now.getFullYear() &&
            dueDate.getMonth() === now.getMonth() &&
            dueDate.getDate() === now.getDate()
    })
);

export const mediateNegBalancesInfoDependent = pipe(
    props(['entries', 'balances']),
    ([entries, balances]) => {
        return pickBy((val, key) => {
            let entry = entries.find(propEq('id', key));
            return Number(val) < 0 && !entry.complete;
        }, balances);
    },
    toPairs,
    map(([id, bal]) => {
        return {
            id,
            bal
        }
    })
);
